import React, { useEffect, useMemo, useState } from "react";
import { Global } from "../helpers/Global";

// ✅ Toast global
import { useToast } from "../hooks/useToast";

// ===== MUI =====
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Stack,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  InputAdornment,
  IconButton,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";

// Icons
import AttachFileIcon from "@mui/icons-material/AttachFile";

export const RegistrarUsuarioMail = () => {
  // =========================
  // Hooks / estado base
  // =========================
  const toast = useToast();

  const [managers, setManagers] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [nuevoUsuario, setNuevoUsuario] = useState({
    nombre: "",
    apellidos: "",
    dni: "",
    email: "",
    password: "",
    rol: "usuario",
    gerente_id: "",
    telefono: "",
  });

  // Avatar (opcional)
  const [avatarFile, setAvatarFile] = useState(null);

  // =========================
  // Carga managers
  // =========================
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${Global.url}usuarios`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });
        if (!res.ok) {
          setManagers([]);
          return;
        }
        const data = await res.json();
        const gerentes = (Array.isArray(data) ? data : []).filter((u) => u.rol === "gerente");
        setManagers(gerentes);
      } catch (err) {
        console.error("Error al obtener managers:", err);
        setManagers([]);
      }
    })();
  }, []);

  // =========================
  // Opciones manager (Autocomplete)
  // =========================
  const managersOrdenados = useMemo(() => {
    return (managers || [])
      .slice()
      .sort((a, b) =>
        `${a.nombre} ${a.apellidos || ""}`.localeCompare(`${b.nombre} ${b.apellidos || ""}`)
      );
  }, [managers]);

  const managerSeleccionado =
    managersOrdenados.find((m) => String(m.id) === String(nuevoUsuario.gerente_id)) || null;

  // =========================
  // Handlers
  // =========================
  const setField = (k, v) => setNuevoUsuario((p) => ({ ...p, [k]: v }));

  const onAvatarChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) {
      setAvatarFile(null);
      return;
    }
    if (!file.type.startsWith("image/")) {
      toast.warning("El archivo seleccionado no es una imagen.");
      setAvatarFile(null);
      return;
    }
    if (file.size > 3 * 1024 * 1024) {
      toast.warning("La imagen no puede superar los 3MB.");
      setAvatarFile(null);
      return;
    }
    setAvatarFile(file);
  };

  // =========================
  // Submit
  // =========================
  const registrarUsuario = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    const email = (nuevoUsuario.email || "").trim().toLowerCase();
    const password = nuevoUsuario.password || "";

    if (!email) {
      toast.warning("El email es obligatorio.");
      return;
    }
    if (!password) {
      toast.warning("La contraseña es obligatoria.");
      return;
    }

    setIsSubmitting(true);

    const payload = {
      ...nuevoUsuario,
      email,
      dni: (nuevoUsuario.dni || "").toUpperCase().replace(/\s|-/g, "") || null,
      gerente_id: nuevoUsuario.gerente_id || null,
      telefono: (nuevoUsuario.telefono || "").trim() || null,
    };

    try {
      const res = await fetch(`${Global.url}usuarios/admin/register-mail`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        toast.error(data?.message || "Error al registrar el usuario.");
        return;
      }

      const createdUser = data?.user;
      toast.success("Usuario registrado correctamente.");

      // Subida de avatar (si hay)
      if (createdUser?.id && avatarFile) {
        try {
          const fd = new FormData();
          fd.append("avatar", avatarFile);

          const avatarRes = await fetch(`${Global.url}usuarios/${createdUser.id}/avatar`, {
            method: "POST",
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
            body: fd,
          });

          if (!avatarRes.ok) {
            const avatarData = await avatarRes.json().catch(() => ({}));
            console.error("Error al subir avatar:", avatarData);
            toast.warning("Usuario creado, pero el avatar no se pudo subir.");
          } else {
            toast.success("Avatar subido correctamente.");
          }
        } catch (err) {
          console.error("Error de red al subir avatar:", err);
          toast.warning("Usuario creado, pero hubo un error subiendo el avatar.");
        }
      }

      // Reset
      setNuevoUsuario({
        nombre: "",
        apellidos: "",
        dni: "",
        email: "",
        password: "",
        rol: "usuario",
        gerente_id: "",
        telefono: "",
      });
      setAvatarFile(null);
    } catch (error) {
      console.error("Error al registrar usuario:", error);
      toast.error("Error de conexión con el servidor.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // =========================
  // Render
  // =========================
  return (
    <Box className="appContainer">
      <Stack spacing={2}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          Registrar nuevo usuario (Email)
        </Typography>

        <Box sx={{ width: "100%", display: "flex", justifyContent: "center" }}>
          <Paper sx={{ p: 2, width: "100%", maxWidth: 900 }}>
            <Box
              component="form"
              onSubmit={registrarUsuario}
              sx={{
                display: "grid",
                gap: 2,
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },

                // 🔑 Alinea todos los campos por el "suelo"
                alignItems: "end",
              }}
            >
              {/* Email + DNI */}
              <TextField
                label="Email (obligatorio)"
                value={nuevoUsuario.email}
                onChange={(e) => setField("email", e.target.value)}
                required
                size="small"
              />
              <TextField
                label="DNI (opcional)"
                value={nuevoUsuario.dni}
                onChange={(e) => setField("dni", e.target.value)}
                placeholder="12345678Z"
                size="small"
              />

              {/* Nombre + Apellidos */}
              <TextField
                label="Nombre"
                value={nuevoUsuario.nombre}
                onChange={(e) => setField("nombre", e.target.value)}
                required
                size="small"
              />
              <TextField
                label="Apellidos"
                value={nuevoUsuario.apellidos}
                onChange={(e) => setField("apellidos", e.target.value)}
                required
                size="small"
              />

              {/* Teléfono + Avatar (misma fila, misma altura) */}
              <TextField
                label="Teléfono (opcional)"
                value={nuevoUsuario.telefono}
                onChange={(e) => setField("telefono", e.target.value)}
                placeholder="Ej: 600 123 456"
                size="small"
              />

              {/* ✅ Avatar como TextField readOnly + botón dentro (endAdornment) */}
              <TextField
                label="Foto / Avatar (opcional)"
                value={
                  avatarFile
                    ? `${avatarFile.name} (${Math.round(avatarFile.size / 1024)} KB)`
                    : ""
                }
                placeholder="Sin archivo"
                size="small"
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton component="label" edge="end" size="small">
                        <AttachFileIcon fontSize="small" />
                        <input hidden type="file" accept="image/*" onChange={onAvatarChange} />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              {/* Password + Rol */}
              <TextField
                label="Contraseña"
                type="password"
                value={nuevoUsuario.password}
                onChange={(e) => setField("password", e.target.value)}
                required
                size="small"
              />

              <FormControl fullWidth size="small">
                <InputLabel id="rol-label">Rol</InputLabel>
                <Select
                  labelId="rol-label"
                  label="Rol"
                  value={nuevoUsuario.rol}
                  onChange={(e) => setField("rol", e.target.value)}
                >
                  <MenuItem value="usuario">Usuario</MenuItem>
                  <MenuItem value="gerente">Gerente</MenuItem>
                  <MenuItem value="admin">Administrador</MenuItem>
                </Select>
              </FormControl>

              {/* Manager (1/2) + Botón (1/2) */}
              <Autocomplete
                size="small"
                options={managersOrdenados}
                getOptionLabel={(u) => `${u.nombre} ${u.apellidos || ""}`.trim()}
                isOptionEqualToValue={(opt, val) => String(opt.id) === String(val.id)}
                value={managerSeleccionado}
                onChange={(_, newValue) => setField("gerente_id", newValue ? String(newValue.id) : "")}
                renderInput={(params) => (
                  <TextField {...params} label="Manager (opcional)" placeholder="Sin manager" size="small" />
                )}
                clearOnEscape
              />

              {/* ✅ Botón alineado abajo, misma línea que Manager */}
              <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  sx={{
                    height: 40,          // 🔑 iguala la altura visual con inputs small
                    minWidth: 160,
                  }}
                >
                  {isSubmitting ? "Registrando..." : "Registrar"}
                </Button>
              </Box>
            </Box>
          </Paper>
        </Box>
      </Stack>
    </Box>
  );
};
